import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

interface VideoModeContextType {
  isVideoMode: boolean;
  isTransitioning: boolean;
  enterVideoMode: () => void;
  exitVideoMode: () => void;
}

const VideoModeContext = createContext<VideoModeContextType | undefined>(undefined);

export function VideoModeProvider({ children }: { children: ReactNode }) {
  const [isVideoMode, setIsVideoMode] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const enterVideoMode = useCallback(() => {
    setIsTransitioning(true);
    // Start transition, then show video after camera animation
    setTimeout(() => {
      setIsVideoMode(true);
      setIsTransitioning(false);
    }, 1500); // Match camera animation duration
  }, []);

  const exitVideoMode = useCallback(() => {
    setIsTransitioning(true);
    setIsVideoMode(false);
    // Reset transition state after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 1500);
  }, []);

  return (
    <VideoModeContext.Provider value={{ isVideoMode, isTransitioning, enterVideoMode, exitVideoMode }}>
      {children}
    </VideoModeContext.Provider>
  );
}

export function useVideoMode() {
  const context = useContext(VideoModeContext);
  if (context === undefined) {
    throw new Error('useVideoMode must be used within a VideoModeProvider');
  }
  return context;
}

